// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The bench's draft, kept on the shared draft as it changes (M13-16, 11 §4).
//
// **The Builder's own rule, taken off the bench.** `BuilderRoute` writes the draft through
// `saveDraft` 1200 ms after the last change, and only once the session has changed something
// (`canUndo`) — so a visitor who merely opened the Builder never acquires a draft that is an
// unedited NEON NOVA, and a gesture is one write rather than sixty. The headset's bench has the
// same two facts: every commit, undo and redo hands the host the draft and whether it differs
// from the machine entered on, and this turns that into the same trailing-edge write. What is
// written is what `/builder` opens (`readDraft`) and what the front door offers, so a machine laid
// out on a headset and a headset put down mid-carry both come back — the carry is the one thing a
// recycle loses (XR-030), because it was never in the draft.
//
// Pure: the timers and the writer are handed in, so a test drives it with fake ones and the
// shell hands it the browser's.

import type { MachineDefinition } from '@core/machine'

/** The Builder's own delay, and this one's: a gesture is one write. */
export const DRAFT_AUTOSAVE_MS = 1200

export interface DraftAutosaveOptions {
  /** `saveDraft`, or a test's record of it. Answers whether the write stuck. */
  readonly save: (definition: MachineDefinition) => Promise<boolean>
  readonly delayMs?: number
  readonly setTimeout?: (fn: () => void, ms: number) => unknown
  readonly clearTimeout?: (handle: unknown) => void
  /** Told once per write that stuck, for a shell that shows the draft as kept. */
  readonly onSaved?: (definition: MachineDefinition) => void
}

export interface DraftAutosave {
  /**
   * The bench changed. `edited` is the host's `canUndo`: false is a bench back at the machine it
   * entered on, which is not a draft and is not written — and cancels a write still pending, so
   * an undo to the start does not save the edit it undid.
   */
  changed(definition: MachineDefinition, edited: boolean): void
  /** Write now, if anything is pending — the session is ending, and 1200 ms is a long time. */
  flush(): void
  /** How many writes have been asked of `save`. */
  readonly writes: number
  dispose(): void
}

export function draftAutosave(options: DraftAutosaveOptions): DraftAutosave {
  const delay = options.delayMs ?? DRAFT_AUTOSAVE_MS
  const later = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms))
  const cancel = options.clearTimeout ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>))
  let pending: { handle: unknown; definition: MachineDefinition } | undefined
  let writes = 0

  const write = (definition: MachineDefinition): void => {
    writes += 1
    void options
      .save(definition)
      .then((stuck) => {
        if (stuck) options.onSaved?.(definition)
      })
      .catch(() => undefined)
  }

  return {
    get writes() {
      return writes
    },
    changed(definition, edited) {
      if (pending !== undefined) {
        cancel(pending.handle)
        pending = undefined
      }
      if (!edited) return
      const handle = later(() => {
        pending = undefined
        write(definition)
      }, delay)
      pending = { handle, definition }
    },
    flush() {
      if (pending === undefined) return
      cancel(pending.handle)
      const { definition } = pending
      pending = undefined
      write(definition)
    },
    dispose() {
      // A pending write goes out on the way down: the page is leaving, and the last commit is
      // the one the author would most expect to find in `/builder`.
      this.flush()
    },
  }
}
